import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { StoreProvider } from "@/context/StoreContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ChatWidget } from "@/components/ChatWidget";

import Home from "@/pages/Home";
import Catalogue from "@/pages/Catalogue";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Favourites from "@/pages/Favourites";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Checkout from "@/pages/Checkout";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Contact from "@/pages/Contact";

import AdminLogin from "@/pages/admin/Login";
import AdminLayout from "@/components/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import ImportSync from "@/pages/admin/ImportSync";
import ProductsAdmin from "@/pages/admin/ProductsAdmin";
import Taxonomies from "@/pages/admin/Taxonomies";
import Settings from "@/pages/admin/Settings";
import Orders from "@/pages/admin/Orders";
import Support from "@/pages/admin/Support";
import Users from "@/pages/admin/Users";
import Customers from "@/pages/admin/Customers";
import Tech from "@/pages/Tech";
import Technicians from "@/pages/admin/Technicians";
import FsmImport from "@/pages/admin/FsmImport";
import StockSignals from "@/pages/admin/StockSignals";
import AiMonitor from "@/pages/admin/AiMonitor";

const RequireAdmin = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-steel-light">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
};

const RequireCustomer = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-steel-light">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
};

const StoreChat = () => {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  return <ChatWidget />;
};

function App() {
  return (
    <StoreProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/catalogue" element={<Catalogue />} />
            <Route path="/category/:slug" element={<Catalogue />} />
            <Route path="/brand/:brand" element={<Catalogue />} />
            <Route path="/product/:sku" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/favourites" element={<Favourites />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/checkout" element={<RequireCustomer><Checkout /></RequireCustomer>} />
            <Route path="/account" element={<RequireCustomer><Account /></RequireCustomer>} />
            <Route path="/account/orders/:id" element={<RequireCustomer><Account /></RequireCustomer>} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/tech" element={<Tech />} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
              <Route index element={<Dashboard />} />
              <Route path="orders" element={<Orders />} />
              <Route path="support" element={<Support />} />
              <Route path="import" element={<Navigate to="/admin/settings" replace />} />
              <Route path="products" element={<ProductsAdmin />} />
              <Route path="taxonomies" element={<Taxonomies />} />
              <Route path="users" element={<Users />} />
              <Route path="customers" element={<Customers />} />
              <Route path="settings" element={<Settings />} />
              <Route path="technicians" element={<Technicians />} />
              <Route path="fsm-import" element={<FsmImport />} />
              <Route path="stock-signals" element={<StockSignals />} />
              <Route path="ai-monitor" element={<AiMonitor />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <StoreChat />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </StoreProvider>
  );
}

export default App;
